import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, PlayCircle } from 'lucide-react'
import { BottomNavigator } from '@/components/BottomNavigator'
import { spacing } from '@/theme'
import { api } from '@/api/axios'
import { useEffect, useState } from 'react'

type VideoClass = {
  id: string
  title: string
  videoUrl: string
  description: string | null
}

const getYoutubeId = (url: string) => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/)
  return match ? match[1] : null
}

const isYouTube = (url: string) => /(youtube\.com|youtu\.be)/.test(url)

export function EscuelaScreen() {
  const navigate = useNavigate()
  const [videos, setVideos] = useState<VideoClass[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/video-classes/active').then((res) => {
      setVideos(res.data.data || [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          padding: spacing[4],
          paddingBottom: '56px',
          background: 'linear-gradient(165deg, #020617 0%, #0f172a 45%, #1e3a8a 100%)',
        }}
      >
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/interacciones')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing[2],
              color: '#bfdbfe',
              marginBottom: spacing[3],
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <ArrowLeft size={18} />
            Volver
          </button>

          <h1 style={{ color: '#e2e8f0', fontSize: 28, fontWeight: 700, marginBottom: spacing[1] }}>Escuela</h1>
          <p style={{ color: '#cbd5e1', fontSize: 14, marginBottom: spacing[4] }}>
            Videos seleccionados para fortalecer tu perfil y habilidades.
          </p>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ borderRadius: 16, overflow: 'hidden', background: 'rgba(15, 23, 42, 0.85)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                  <div style={{ aspectRatio: '16/9', background: 'rgba(59, 130, 246, 0.05)' }} />
                  <div style={{ padding: spacing[3] }}>
                    <div style={{ width: '60%', height: 14, borderRadius: 8, background: 'rgba(59, 130, 246, 0.1)', marginBottom: 8 }} />
                    <div style={{ width: '90%', height: 10, borderRadius: 8, background: 'rgba(59, 130, 246, 0.05)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: spacing[10], color: '#64748b' }}>
              <PlayCircle size={48} style={{ margin: '0 auto', opacity: 0.3 }} />
              <p style={{ marginTop: spacing[2], fontSize: 15 }}>Proximamente...</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
              {videos.map((video) => {
                const youtubeId = getYoutubeId(video.videoUrl)
                return (
                  <motion.article
                    key={video.id}
                    whileHover={{ y: -3 }}
                    style={{
                      borderRadius: 16,
                      border: '1px solid rgba(59, 130, 246, 0.35)',
                      backgroundColor: 'rgba(15, 23, 42, 0.85)',
                      overflow: 'hidden',
                    }}
                  >
                    {youtubeId ? (
                      <div style={{ aspectRatio: '16/9', backgroundColor: '#000' }}>
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${youtubeId}`}
                          title={video.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ border: 0 }}
                        />
                      </div>
                    ) : (
                      <div style={{ aspectRatio: '16/9', backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <video
                          src={video.videoUrl}
                          controls
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                    )}
                    <div style={{ padding: spacing[3] }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[1] }}>
                        <PlayCircle size={18} color="#93c5fd" />
                        <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 17, fontWeight: 700 }}>{video.title}</h3>
                      </div>
                      {video.description && (
                        <p style={{ margin: 0, color: '#cbd5e1', fontSize: 13, lineHeight: 1.45 }}>{video.description}</p>
                      )}
                    </div>
                  </motion.article>
                )
              })}
            </div>
          )}
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}
